import { RepaymentStrategy } from 'modules/cm/RepaymentStrategy/RepaymentStrategy';
import { Current as CurrentIncome } from 'modules/cm/domain/Case/Applicant/Income/Current/Current';
import { Future as FutureIncome } from 'modules/cm/domain/Case/Applicant/Income/Future/Future';
import { CaseStatuses } from 'modules/cm/domain/Case/CaseStatuses';
import { LoanDetails } from 'modules/cm/domain/Case/LoanDetails';
import { ReadonlyFields } from 'modules/cm/domain/Case/ReadonlyFields';
import { FIELDS } from 'modules/cm/fields/types';
import { AssurancePolicy } from 'modules/cm/pages/CaseDetailsPage/subPages/LifeAssurance/LifeAssurance.types';
import { Nullable } from 'utils/types';

export interface ModuleProps {
  modulePrefix: string;
}

type Address = {
  flatNumber: Nullable<string>;
  buildingName: Nullable<string>;
  buildingNumber: Nullable<string>;
  street: Nullable<string>;
  city: Nullable<string>;
  postcode: Nullable<string>;
};

export type ApplicantAddress = Address & {
  id: number;
  movedInDate: Nullable<string>;
  proofOfAddress: Nullable<string>;
};

type AplicantCurrentAddress = ApplicantAddress & {
  proofOfAddress: Nullable<string>;
};

type PowerOfAttorney = {
  id: number;
  [FIELDS.APPLICANT_POWER_OF_ATTORNEY_NAME]: Nullable<string>;
  [FIELDS.APPLICANT_POWER_OF_ATTORNEY_HAS_CUSTOMISED_SERVICES]: Nullable<boolean>;
  [FIELDS.APPLICANT_POWER_OF_ATTORNEY_CUSTOMISED_SERVICES_REQUEST]: Nullable<string>;
  [FIELDS.APPLICANT_POWER_OF_ATTORNEY_PHONE_NUMBER]: Nullable<string>;
  [FIELDS.APPLICANT_POWER_OF_ATTORNEY_EMAIL]: Nullable<string>;
  [FIELDS.APPLICANT_POWER_OF_ATTORNEY_POSTCODE]: Nullable<string>;
  [FIELDS.APPLICANT_POWER_OF_ATTORNEY_FLAT_NUMBER]: Nullable<string>;
  [FIELDS.APPLICANT_POWER_OF_ATTORNEY_BUILDING_NAME]: Nullable<string>;
  [FIELDS.APPLICANT_POWER_OF_ATTORNEY_BUILDING_NUMBER]: Nullable<string>;
  [FIELDS.APPLICANT_POWER_OF_ATTORNEY_STREET]: Nullable<string>;
  [FIELDS.APPLICANT_POWER_OF_ATTORNEY_CITY]: Nullable<string>;
};

export type Liability = {
  [FIELDS.EXPENDITURE_ID]: number;
  [FIELDS.EXPENDITURE_TYPE]: Nullable<string>;
  [FIELDS.EXPENDITURE_CREDIT_LIMIT]: Nullable<number>;
  [FIELDS.EXPENDITURE_BALANCE]: Nullable<number>;
  [FIELDS.EXPENDITURE_DESCRIPTION]: Nullable<string>;
  [FIELDS.EXPENDITURE_AMOUNT]: Nullable<number>;
  [FIELDS.EXPENDITURE_FREQUENCY]: Nullable<string>;
  [FIELDS.EXPENDITURE_END_DATE]: Nullable<string>;
  [FIELDS.EXPENDITURE_TOTAL_NUMBER_OF_PROPERTIES]: Nullable<number>;
  [FIELDS.EXPENDITURE_WILL_BE_CLEARED_BEFORE_LOAN]: Nullable<boolean>;
  [FIELDS.EXPENDITURE_WILL_BE_CLEARED_BY_LOAN]: Nullable<boolean>;
};

type Asset = {
  [FIELDS.ASSETS_ID]: number;
  [FIELDS.ASSETS_TYPE]: Nullable<string>;
  [FIELDS.ASSETS_VALUE]: Nullable<number>;
  [FIELDS.ASSETS_DESCRIPTION]: Nullable<string>;
  [FIELDS.ASSETS_WILL_BE_USED_TO_PAY_LOAN]: Nullable<boolean>;
  [FIELDS.ASSETS_OUTSTANDING_MORTGAGE_BALANCE]: Nullable<number>;
  [FIELDS.ASSETS_PROPERTY_POSTCODE]: Nullable<string>;
  [FIELDS.ASSETS_PROPERTY_CITY]: Nullable<string>;
  [FIELDS.ASSETS_PROPERTY_FLAT_NUMBER]: Nullable<string>;
  [FIELDS.ASSETS_PROPERTY_BUILDING_NAME]: Nullable<string>;
  [FIELDS.ASSETS_PROPERTY_BUILDING_NUMBER]: Nullable<string>;
  [FIELDS.ASSETS_PROPERTY_STREET]: Nullable<string>;
};

export type CurrentPension = {
  [FIELDS.CURRENT_STATE_PENSION]: Nullable<number>;
  [FIELDS.CURRENT_DB_NAME_OF_SCHEME]: Nullable<string>;
  [FIELDS.CURRENT_DB_ANNUAL_INCOME]: Nullable<number>;
  [FIELDS.CURRENT_DB_PERCENT_PASSED_ON_DEATH]: Nullable<number>;
  [FIELDS.CURRENT_DC_NAME_OF_SCHEME]: Nullable<string>;
  [FIELDS.CURRENT_DC_CURRENT_VALUE]: Nullable<number>;
  [FIELDS.CURRENT_DC_PERCENT_PASSED_ON_DEATH]: Nullable<number>;
  [FIELDS.CURRENT_DC_ANNUAL_INCOME_DRAWN]: Nullable<number>;
  [FIELDS.CURRENT_ANNUITY_NAME_OF_ANNUITY_PROVIDER]: Nullable<string>;
  [FIELDS.CURRENT_ANNUITY_GROSS_ANNUAL_INCOME]: Nullable<number>;
  [FIELDS.CURRENT_ANNUITY_PERCENT_PASSED_ON_DEATH]: Nullable<number>;
};

export type FuturePension = {
  [FIELDS.FUTURE_STATE_PENSION]: Nullable<number>;
  [FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX]: FuturePensionDefinedBenefit[];
  [FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX]: FuturePensionDefinedContribution[];
  [FIELDS.FUTURE_ANNUITY_PREFIX]: FuturePensionAnnuity[];
  [FIELDS.LUMP_SUM_DRAWDOWN]: Nullable<number>;
  [FIELDS.REPAYMENT_LUMP_SUM]: Nullable<number>;
};

export type Applicant = CurrentPension &
  FuturePension &
  Employed &
  CurrentIncome &
  FutureIncome & {
    id: number;
    name: string;
    surname: string;
    title: string;
    [FIELDS.ASSETS_PREFIX]: Asset[];
    [FIELDS.APPLICANT_MIDDLE_NAME]: Nullable<string>;
    [FIELDS.APPLICANT_PREVIOUS_NAME]: Nullable<string>;
    [FIELDS.APPLICANT_PROOF_OF_ID]: Nullable<string>;
    [FIELDS.APPLICANT_DATE_OF_BIRTH]: Nullable<string>;
    [FIELDS.APPLICANT_GENDER]: Nullable<string>;
    [FIELDS.APPLICANT_MARITAL_STATUS]: Nullable<string>;
    [FIELDS.APPLICANT_CURRENT_ADDRESS]: AplicantCurrentAddress;
    [FIELDS.APPLICANT_PREVIOUS_ADDRESSES]: ApplicantAddress[];
    [FIELDS.APPLICANT_NATIONALITY]: Nullable<string>;
    [FIELDS.APPLICANT_IS_UK_RESIDENT]: Nullable<boolean>;
    [FIELDS.APPLICANT_HAS_INDEFINITE_LEAVE_TO_REMAIN]: Nullable<boolean>;
    [FIELDS.APPLICANT_PHONE_HOME]: Nullable<string>;
    [FIELDS.APPLICANT_IS_PHONE_HOME_PEPPER]: Nullable<boolean>;
    [FIELDS.APPLICANT_PHONE_MOBILE]: Nullable<string>;
    [FIELDS.APPLICANT_IS_PHONE_MOBILE_PEPPER]: Nullable<boolean>;
    [FIELDS.APPLICANT_PHONE_WORK]: Nullable<string>;
    [FIELDS.APPLICANT_IS_PHONE_WORK_PEPPER]: Nullable<boolean>;
    [FIELDS.APPLICANT_EMAIL]: Nullable<string>;
    [FIELDS.APPLICANT_IS_EMAIL_PEPPER]: Nullable<boolean>;
    [FIELDS.APPLICANT_ADDITIONAL_EMAIL]: Nullable<string>;
    [FIELDS.APPLICANT_ADDITIONAL_EMAIL_PEPPER]: Nullable<boolean>;
    [FIELDS.APPLICANT_IS_CRIMINAL]: Nullable<string>;
    [FIELDS.APPLICANT_CRIMINAL_CONVICTIONS_DETAIL]: Nullable<string>;
    [FIELDS.APPLICANT_HAS_ADVERSE_CREDIT]: Nullable<string>;
    [FIELDS.APPLICANT_ADVERSE_CREDIT_DETAILS]: Nullable<string>;
    [FIELDS.APPLICANT_BANKRUPTCY_AMOUNT]: Nullable<number>;
    [FIELDS.APPLICANT_BANKRUPTCY_LATEST_DATE]: Nullable<string>;
    [FIELDS.APPLICANT_CCJ_AMOUNT]: Nullable<number>;
    [FIELDS.APPLICANT_CCJ_LATEST_DATE]: Nullable<string>;
    [FIELDS.APPLICANT_DEFAULT_AMOUNT]: Nullable<number>;
    [FIELDS.APPLICANT_DEFAULT_LAST_DATE]: Nullable<string>;
    [FIELDS.APPLICANT_MORTGAGE_ARREARS_AMOUNT]: Nullable<number>;
    [FIELDS.APPLICANT_MORTGAGE_ARREARS_LATEST_DATE]: Nullable<string>;
    [FIELDS.APPLICANT_POWER_OF_ATTORNEY]: PowerOfAttorney;
    [FIELDS.EXPENDITURES_PREFIX]: Liability[];
    [FIELDS.APPLICANT_NUMBER_OF_DEPENDANTS]: Nullable<number>;
    [FIELDS.LUMP_SUM_DRAWDOWN]: Nullable<number>;
    [FIELDS.REPAYMENT_LUMP_SUM]: Nullable<number>;
  };

export type FuturePensionDefinedBenefit = {
  id: number;
  nameOfScheme: Nullable<string>;
  annualIncome: Nullable<number>;
  startDate: Nullable<string>;
  percentageValueOfPassedOnDeath: Nullable<number>;
  repaymentLumpSum: Nullable<number>;
};

export type FuturePensionDefinedContribution = {
  id: number;
  [FIELDS.NAME_OF_SCHEME]: Nullable<string>;
  [FIELDS.CURRENT_VALUE]: Nullable<number>;
  [FIELDS.START_DATE]: Nullable<string>;
  [FIELDS.ONGOING_MONTHLY_CONTRIBUTIONS]: Nullable<number>;
  [FIELDS.PERCENTAGE_VALUE_OF_PASSED_ON_DEATH]: Nullable<number>;
  [FIELDS.FUTURE_ANNUAL_INCOME_DRAWN]: Nullable<number>;
  [FIELDS.USE_OF_FUND]: Nullable<string>;
};

export type FuturePensionAnnuity = {
  id: number;
  [FIELDS.FUTURE_ANNUITY_NAME_OF_ANNUITY_PROVIDER]: Nullable<string>;
  [FIELDS.FUTURE_ANNUITY_GROSS_ANNUAL_INCOME]: Nullable<number>;
  [FIELDS.FUTURE_ANNUITY_PERCENT_PASSED_ON_DEATH]: Nullable<number>;
};

export type Solicitor = {
  [FIELDS.SOLICITOR_NAME]: Nullable<string>;
  [FIELDS.SOLICITOR_EMAIL]: Nullable<string>;
  [FIELDS.SOLICITOR_CONTACT_NUMBER]: Nullable<string>;
  [FIELDS.SOLICITOR_ADDRESS_CITY]: Nullable<string>;
  [FIELDS.SOLICITOR_FLAT_NUMBER]: Nullable<string>;
  [FIELDS.SOLICITOR_BUILDING_NUMBER]: Nullable<string>;
  [FIELDS.SOLICITOR_BUILDING_NAME]: Nullable<string>;
  [FIELDS.SOLICITOR_STREET]: Nullable<string>;
  [FIELDS.SOLICITOR_ADDRESS_POSTCODE]: Nullable<string>;
};

export type Admin = {
  [FIELDS.ADMIN_OFFER_DATE]: Nullable<string>;
  [FIELDS.ADMIN_COT_REC_DATE]: Nullable<string>;
  [FIELDS.ADMIN_FUNDS_REQ_DATE]: Nullable<string>;
  [FIELDS.ADMIN_EXPECTED_COMPLETION_DATE]: Nullable<string>;
  [FIELDS.ADMIN_COMPLETION_DATE]: Nullable<string>;
  [FIELDS.ADMIN_PEPPER_UPLOAD_DATE]: Nullable<string>;
  [FIELDS.ADMIN_PEPPER_ACCOUNT_ID]: Nullable<string>;
  [FIELDS.ADMIN_ORIGINATOR_ID]: Nullable<string>;
  [FIELDS.ADMIN_DECLINE_DATE]: Nullable<string>;
  [FIELDS.ADMIN_SWAP_COST]: Nullable<number>;
  [FIELDS.ADMIN_EXPECTED_MATURITY]: Nullable<number>;
};

type Contact = {
  adviserName: Nullable<string>;
  adviserFee: Nullable<number>;
  adviserPhone: Nullable<string>;
  adviserEmail: Nullable<string>;
  standardCommissionTerms: Nullable<string>;
  enhancedCommissionTerms: Nullable<string>;
  ongoingCommissionTermsUpfrontFee: Nullable<string>;
  ongoingCommissionTermsOngoingFee: Nullable<string>;
  firmName: Nullable<string>;
  firmFcaNumber: Nullable<string>;
  firmAddress: Nullable<string>;
  networkFcaNumber: Nullable<string>;
  adviserFeeLastPaymentMoment: Nullable<string>;
  packagerFee: Nullable<number>;
  packagerFeeLastPaymentMoment: Nullable<string>;
} & { solicitor: Solicitor } & { [FIELDS.ADMIN_PREFIX]: Admin };

type PropertyOccupant = {
  id: number;
  [FIELDS.PROPERTY_OCCUPANT_FULLNAME]: Nullable<string>;
  [FIELDS.PROPERTY_OCCUPANT_DATE_OF_BIRTH]: Nullable<string>;
};

type Property = {
  [FIELDS.PROPERTY_FLAT_NUMBER]: Nullable<string>;
  [FIELDS.PROPERTY_BUILDING_NAME]: Nullable<string>;
  [FIELDS.PROPERTY_BUILDING_NUMBER]: Nullable<string>;
  [FIELDS.PROPERTY_STREET]: Nullable<string>;
  [FIELDS.PROPERTY_CITY]: Nullable<string>;
  [FIELDS.PROPERTY_POSTCODE]: Nullable<string>;
  [FIELDS.PROPERTY_HAS_OCCUPANTS_OVER17]: Nullable<string>;
  [FIELDS.PROPERTY_TYPE]: Nullable<string>;
  [FIELDS.PROPERTY_IS_PRIMARY_HOME]: Nullable<string>;
  [FIELDS.PROPERTY_FLOOR_NUMBER]: Nullable<number>;
  [FIELDS.PROPERTY_NUMBER_OF_FLOORS_IN_THE_BUILDING]: Nullable<number>;
  [FIELDS.PROPERTY_HAS_LIFT_ACCESS]: Nullable<string>;
  [FIELDS.PROPERTY_TENURE_TYPE]: Nullable<string>;
  [FIELDS.PROPERTY_REMAINING_YEARS]: Nullable<number>;
  [FIELDS.PROPERTY_IS_EX_LOCAL_AUTHORITY]: Nullable<boolean>;
  [FIELDS.PROPERTY_IS_RETIREMENT_HOME]: Nullable<boolean>;
  [FIELDS.PROPERTY_IS_RESTRICTED_COVENANT]: Nullable<boolean>;
  [FIELDS.PROPERTY_IS_ABOVE_COMMERCIAL_PREMISES]: Nullable<boolean>;
  [FIELDS.PROPERTY_COMMERCIAL_PREMISES_DETAILS]: Nullable<string>;
  [FIELDS.PROPERTY_HAS_COMMERCIAL_USAGE_ABOVE25_PERCENT]: Nullable<string>;
  [FIELDS.PROPERTY_COMMERCIAL_USAGE_DETAILS]: Nullable<string>;
  [FIELDS.PROPERTY_NUMBER_OF_KITCHENS]: Nullable<number>;
  [FIELDS.PROPERTY_NUMBER_OF_BEDROOMS]: Nullable<number>;
  [FIELDS.PROPERTY_HAS_AGRICULTURAL_RESTRICTIONS]: Nullable<string>;
  [FIELDS.PROPERTY_PROPERTY_YEAR_BUILT]: Nullable<number>;
  [FIELDS.PROPERTY_WARRANTY_PROVIDER]: Nullable<string>;
  [FIELDS.PROPERTY_IS_PROPERTY_NEW_BUILD]: Nullable<string>;
  [FIELDS.PROPERTY_PROPERTY_CONSTRUCTION]: Nullable<string>;
  [FIELDS.PROPERTY_PROPERTY_DETAILS]: Nullable<string>;
  [FIELDS.PROPERTY_INSTRUCTION_DATE]: Nullable<string>;
  [FIELDS.PROPERTY_INSPECTION_DATE]: Nullable<string>;
  [FIELDS.PROPERTY_VALUATION_TYPE]: Nullable<string>;
  [FIELDS.PROPERTY_HAS_SPECIALIST_REPORT]: Nullable<string>;
  [FIELDS.PROPERTY_TITLE_NUMBER]: Nullable<string>;
  [FIELDS.PROPERTY_ESTIMATED_PROPERTY_VALUE]: Nullable<number>;
  [FIELDS.PROPERTY_PROPERTY_PURCHASE_PRICE]: Nullable<number>;
  [FIELDS.PROPERTY_ACTUAL_PROPERTY_VALUE]: Nullable<number>;
  [FIELDS.PROPERTY_OCCUPANTS_PREFIX]: PropertyOccupant[];
};

type Resource = {
  option: string;
  value: string | boolean | number;
};

export type Employed = {
  [FIELDS.APPLICANT_EMPLOYMENT_STATUS]: Nullable<string>;
  [FIELDS.APPLICANT_EMPLOYED_JOB_TITLE]: Nullable<string>;
  [FIELDS.APPLICANT_EMPLOYED_COMPANY_NAME]: Nullable<string>;
  [FIELDS.APPLICANT_EMPLOYED_COMPANY_ADDRESS]: Nullable<string>;
  [FIELDS.APPLICANT_EMPLOYED_NATURE]: Nullable<string>;
  [FIELDS.APPLICANT_EMPLOYED_START_DATE]: Nullable<string>;
  [FIELDS.APPLICANT_EMPLOYED_ANTICIPATED_AGE]: Nullable<number>;
  [FIELDS.APPLICANT_SELF_EMPLOYED_JOB_TITLE]: Nullable<string>;
  [FIELDS.APPLICANT_SELF_EMPLOYED_COMPANY_NAME]: Nullable<string>;
  [FIELDS.APPLICANT_SELF_EMPLOYED_COMPANY_ADDRESS]: Nullable<string>;
  [FIELDS.APPLICANT_SELF_EMPLOYED_NATURE]: Nullable<string>;
  [FIELDS.APPLICANT_SELF_EMPLOYED_START_DATE]: Nullable<string>;
  [FIELDS.APPLICANT_BUSINESS_TYPE]: Nullable<string>;
  [FIELDS.APPLICANT_SELF_EMPLOYED_ANTICIPATED_AGE]: Nullable<number>;
};

export type CaseStatus = string | CaseStatuses.NotSet;

export type CaseDetails = {
  case: {
    status: CaseStatus;
    identifier: string;
    updatedAt: string;
    applicants: Applicant[];
    loanDetails: LoanDetails;
    property: Property;
    contact: Contact;
    assurancePolicies: AssurancePolicy[];
    rationale: string;
    offerPackUrl: string;
    preOfferEsisUrl: string;
    repaymentStrategy: Nullable<RepaymentStrategy>;
  };
  resources: {
    gender: Resource[];
    applicationTypes: Resource[];
    caseStatuses: Resource[];
    creditPolicyExemptiones: Resource[];
    pepperPurposesOfTheLoan: Resource[];
    productFeePaymentMethods: Resource[];
    purposesOfTheLoan: Resource[];
    sourcesOfDeposit: Resource[];
    valuationFees: Resource[];
    underwriters: Resource[];
    creditFeePayMoment: Resource[];
    originatorIds: Resource[];
    assetTypes: Resource[];
    products: Resource[];
    title: Resource[];
    proofOfId: Resource[];
    proofOfAddress: Resource[];
    martialStatus: Resource[];
    nationalities: Resource[];
    expenditurePaymentFrequencies: Resource[];
    expenditureTypes: Resource[];
    useOfFunds: Resource[];
    employmentStatuses: Resource[];
    employmentNatures: Resource[];
    businessTypes: Resource[];
    propertyTypes: Resource[];
    tenureTypes: Resource[];
    propertyConstructions: Resource[];
    valuationTypes: Resource[];
    assurancePolicyTypes: Resource[];
    assuredPersons: Resource[];
    basisCovers: Resource[];
    fcaLoanPurposes: Resource[];
    existingMortgageStatuses: Resource[];
    authorityLevels: Resource[];
    vulnerabilityCategories: Resource[];
    informationProvidedBy: Resource[];
    productTermLengths: Resource[];
    tiers: Resource[];
  };
  readonlyFields?: ReadonlyFields;
  isSaving?: boolean;
};

export type Props = {
  caseDetails: CaseDetails;
  initialCaseDetails: CaseDetails;
  url: string;
  onCaseRefresh: () => void;
  onTabChange: () => void;
  isDuringSave: boolean;
};

export enum CASE_STATUS {
  CASE_COMPLETED = 'COMPLETED',
}
